<template>
  <div
    v-if="buttons.length"
    :class="inline
      ? 'flex flex-wrap items-center gap-2'
      : 'mb-4 flex w-full flex-col gap-2'"
  >
    <template v-for="button in buttons" :key="button.id">
      <UButton
        v-if="button.actionType === 'link'"
        :label="button.label"
        :to="resolveLink(button.linkUrl)"
        :target="isExternal(button.linkUrl) ? '_blank' : undefined"
        :rel="isExternal(button.linkUrl) ? 'noopener noreferrer' : undefined"
        :variant="button.variant === 'outline' ? 'solid' : (button.variant || 'solid')"
        :color="button.color || 'primary'"
        size="sm"
        :block="!inline"
        :class="inline ? 'text-sm' : 'w-full justify-center text-sm'"
      />
      <UButton
        v-else
        :label="button.label"
        :variant="button.variant === 'outline' ? 'solid' : (button.variant || 'solid')"
        :color="button.color || 'primary'"
        size="sm"
        :block="!inline"
        :class="inline ? 'text-sm' : 'w-full justify-center text-sm'"
        @click="openModal(button)"
      />
    </template>

    <UModal
      v-model:open="modalOpen"
      :title="activeButton?.modalTitle || activeButton?.label || 'Informații'"
      :ui="{ content: 'max-w-2xl' }"
    >
      <template #body>
        <div
          class="prose-cms max-w-none"
          v-html="modalHtml"
        />
      </template>
      <template #footer>
        <UButton label="Închide" color="primary" @click="modalOpen = false" />
      </template>
    </UModal>
  </div>
</template>

<script setup lang="ts">
import type { ProductCtaButton } from '~/types'
import { enrichHtmlMediaUrls } from '~/utils/htmlMedia'

const props = withDefaults(defineProps<{
  buttons: ProductCtaButton[]
  inline?: boolean
}>(), {
  inline: false,
})

const shopPath = useShopPath()
const config = useRuntimeConfig()
const apiOrigin = config.public.apiOrigin as string

const modalOpen = ref(false)
const activeButton = ref<ProductCtaButton | null>(null)

const modalHtml = computed(() =>
  enrichHtmlMediaUrls(activeButton.value?.modalContentHtml || '', apiOrigin),
)

function isExternal(url?: string | null) {
  if (!url) return false
  return /^https?:\/\//i.test(url) || url.startsWith('//')
}

function resolveLink(url?: string | null) {
  if (!url) return undefined
  if (isExternal(url) || url.startsWith('mailto:') || url.startsWith('tel:')) return url
  return shopPath(url.startsWith('/') ? url : `/${url}`)
}

function openModal(button: ProductCtaButton) {
  activeButton.value = button
  modalOpen.value = true
}

watch(
  () => props.buttons,
  () => {
    modalOpen.value = false
    activeButton.value = null
  },
)
</script>
